import { create } from "zustand";
import type { RecurringIncomeItem } from "@boo/shared";
import {
  fetchRecurringIncomes,
  createRecurringIncome,
  updateRecurringIncome,
  deleteRecurringIncome,
  applyRecurringIncomeForPeriod,
  type CreateRecurringIncomePayload,
  type ApplyResult,
} from "../../../services/recurringIncomeApi";

interface RecurringIncomeState {
  items: RecurringIncomeItem[];
  loading: boolean;
  applying: boolean;
  error: string | null;
  modalOpen: boolean;
  editingItem: RecurringIncomeItem | null;

  loadItems: () => Promise<void>;
  applyForPeriod: (year: number, month: number) => Promise<ApplyResult>;
  openCreateModal: () => void;
  openEditModal: (item: RecurringIncomeItem) => void;
  closeModal: () => void;
  saveItem: (payload: CreateRecurringIncomePayload) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
}

export const useRecurringIncomeStore = create<RecurringIncomeState>(
  (set, get) => ({
    items: [],
    loading: false,
    applying: false,
    error: null,
    modalOpen: false,
    editingItem: null,

    async loadItems() {
      set({ loading: true, error: null });
      try {
        const items = await fetchRecurringIncomes();
        set({ items, loading: false });
      } catch (err) {
        set({
          error: err instanceof Error ? err.message : "Failed to load",
          loading: false,
        });
      }
    },

    async applyForPeriod(year, month) {
      set({ applying: true });
      try {
        const result = await applyRecurringIncomeForPeriod(year, month);
        set({ applying: false });
        return result;
      } catch (err) {
        set({ applying: false });
        throw err;
      }
    },

    openCreateModal() {
      set({ modalOpen: true, editingItem: null });
    },

    openEditModal(item) {
      set({ modalOpen: true, editingItem: item });
    },

    closeModal() {
      set({ modalOpen: false, editingItem: null });
    },

    async saveItem(payload) {
      const { editingItem } = get();
      if (editingItem) {
        const updated = await updateRecurringIncome(editingItem.id, payload);
        set((s) => ({
          items: s.items.map((i) => (i.id === updated.id ? updated : i)),
        }));
      } else {
        const created = await createRecurringIncome(payload);
        set((s) => ({ items: [...s.items, created] }));
      }
      get().closeModal();
    },

    async removeItem(id) {
      await deleteRecurringIncome(id);
      set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
    },
  }),
);
